import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtShort } from "@/lib/scheduling";

export const SessionNoteEditor = ({ appointmentId }) => {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [editing, setEditing] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["notes", "appointment", appointmentId], queryFn: async () => (await api.get(`/messages/notes/appointments/${appointmentId}`)).data });
  const note = data?.note;
  useEffect(() => { if (note) setText(note.body); }, [note]);
  const save = useMutation({
    mutationFn: () => api.put(`/messages/notes/appointments/${appointmentId}`, { body: text }),
    onSuccess: () => { toast.success("Session note saved."); setEditing(false); qc.invalidateQueries({ queryKey: ["notes"] }); },
    onError: (e) => toast.error(apiError(e)),
  });
  if (isLoading || !data) return <p className="text-sm text-ink-500" role="status">Loading note…</p>;
  return (
    <div className="border border-sage-400/70 bg-cream-100 p-4" data-testid="session-note">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-serif text-lg font-semibold">Session Note <span className="font-sans text-xs font-normal text-ink-500">· private · encrypted · counselor and Care Director only</span></p>
        {note && <p className="text-xs text-ink-500">{note.author} · {fmtShort(note.last_edited_at || note.created_at)}{note.edits ? ` · edited ${note.edits}×` : ""}</p>}
      </div>
      {note && !editing ? (
        <>
          <p className="mt-2 whitespace-pre-wrap break-words text-ink-900" data-testid="session-note-body">{note.body}</p>
          {data.can_write && <button type="button" className="btn-quiet mt-2 min-h-[36px] px-2 text-sm" onClick={() => setEditing(true)} data-testid="session-note-edit">Edit Note</button>}
        </>
      ) : data.can_write ? (
        <>
          <label htmlFor={`note-${appointmentId}`} className="sr-only">Session note</label>
          <textarea id={`note-${appointmentId}`} className="input mt-2 min-h-[140px]" maxLength={8000} value={text} onChange={(e) => setText(e.target.value)} placeholder="What you want to remember from this session. Only you and the Care Director can read this." data-testid="session-note-input" />
          <div className="mt-2 flex gap-2">
            <button type="button" className="btn-primary min-h-[40px]" disabled={!text.trim() || save.isPending} onClick={() => save.mutate()} data-testid="session-note-save">Save Note</button>
            {note && <button type="button" className="btn-quiet min-h-[40px]" onClick={() => { setEditing(false); setText(note.body); }}>Cancel</button>}
          </div>
        </>
      ) : <p className="mt-2 text-sm text-ink-500" data-testid="session-note-empty">No note for this session.</p>}
    </div>
  );
};

export const CaseNotesList = ({ caseId }) => {
  const { data = [], isLoading, isError } = useQuery({ queryKey: ["notes", "case", caseId], queryFn: async () => (await api.get(`/messages/notes/cases/${caseId}`)).data, retry: false });
  if (isError) return null;
  if (isLoading) return <p className="text-sm text-ink-500" role="status">Loading session notes…</p>;
  return (
    <div className="mt-4 border-t border-sage-400/60 pt-4" data-testid="case-notes">
      <p className="font-serif text-lg font-semibold">Session Notes <span className="font-sans text-xs font-normal text-ink-500">· counselor and Care Director only · each view is logged</span></p>
      {data.length === 0 && <p className="mt-1 text-sm text-ink-500" data-testid="case-notes-empty">No session notes yet.</p>}
      <ol className="mt-2 space-y-3">
        {data.map((n) => (
          <li key={n.id} className="border-l-2 border-olive-900 pl-3" data-testid={`case-note-${n.id}`}>
            <p className="text-xs text-ink-500">{n.session_number ? `Session ${n.session_number} · ` : ""}{n.start_at ? fmtShort(n.start_at) : ""} · {n.author}{n.edits ? ` · edited` : ""}</p>
            <p className="mt-1 whitespace-pre-wrap break-words text-sm text-ink-900">{n.body}</p>
          </li>
        ))}
      </ol>
    </div>
  );
};
